import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from 'src/app/services/data.service';
import { ToastService } from 'src/app/services/toast.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SentMapInvitationsResponse } from './models/map-invite.model';

/** Invite people to a map by email (opened from the maps list and the map's
 * Modify page). Game invites have their own modal. */
@Component({
    selector: 'app-map-invite-modal',
    templateUrl: './map-invite-modal.component.html',
    styleUrls: ['./map-invite-modal.component.scss'],
    standalone: false
})
export class MapInviteModalComponent implements OnInit {
  @Input() map_id: string;
  emailinput: any;
  emailRows: any;
  emailalert: string;
  loading = false; // Variable to control the visibility of the loading spinner
  user_id: any;
  isError: boolean = false;
  sentMapInvitations: SentMapInvitationsResponse | null = null;
  isFetching: boolean = false;

  // ---- for the template ---------------------------------------------------------
  get slotsTotal(): number {
    return this.sentMapInvitations?.slots_total || 5;
  }
  get slotsUsed(): number {
    return this.sentMapInvitations?.slots_used ?? (this.emailRows || []).filter((r) => r.isExisting).length;
  }
  get hasExisting(): boolean {
    return (this.emailRows || []).some((r) => r.isExisting);
  }
  get newRowCount(): number {
    return (this.emailRows || []).filter((r) => !r.isExisting).length;
  }

  constructor(
    private activemodel: NgbActiveModal,
    private dataserv: DataService,
    private toastService: ToastService,
    private currentUserService: CurrentUserService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.currentUserService.currentUser.subscribe((data) => {
      if (data) {
        this.user_id = Number(data.id);
        this.getSentMapInvitations();
      }
      this.cdr.detectChanges();
    });
    this.emailRows = [
      {
        map_id: this.map_id,
        user_id: this.user_id,
        email: '',
      },
    ];
  }

  sendEmail() {
    // Reset email error message
    this.emailalert = '';

    // New rows with an address (blank extra rows are just ignored).
    const toInvite = (this.emailRows || [])
      .filter((row) => row.status == 'ADDED' && (row.email || '').trim())
      .map((row) => ({ ...row, email: row.email.trim() }));
    if (!toInvite.length) {
      this.emailalert = 'Enter an email address to invite.';
      return;
    }
    const bad = toInvite.find((row) => !this.isValidEmail(row.email));
    if (bad) {
      this.emailalert = `"${bad.email}" isn't a valid email address.`;
      return;
    }
    const seen = new Set((this.emailRows || []).filter((r) => r.isExisting).map((r) => (r.email || '').toLowerCase()));
    for (const row of toInvite) {
      const key = row.email.toLowerCase();
      if (seen.has(key)) {
        this.emailalert = `${row.email} is already on the list.`;
        return;
      }
      seen.add(key);
    }

    this.loading = true; // Show loading spinner before sending emails
    this.isError = false;
    this.dataserv.sendMapInvites({ map_invitations: toInvite }).subscribe(
      (res: any) => {
        this.loading = false
        if (res.map_responses.length > 0) {
          res.map_responses.forEach((response) => {
            this.toastService.showSuccess(
              response.message ? response.message : response.error,
              response.message ? 'success' : 'danger'
            );
            this.isError = response.message ? false : true;
          });
        }
        this.closemodel(!this.isError)
        this.isError = false;
        this.cdr.detectChanges();
      },
      (error) => {
        this.loading = false
        this.isError = true;
        this.toastService.showSuccess(
          error.error.error || error.message || 'Something went wrong !'
        );
        this.cdr.detectChanges();
      }
    );
  }

  getSentMapInvitations() {
    this.isFetching = true;
    this.dataserv.getSentMapInvitations(this.user_id, this.map_id).subscribe(
      (data: SentMapInvitationsResponse) => {
        this.isFetching = false;

        if (data && data.invitations) {
          this.sentMapInvitations = data;

          // Pre-fill emailRows with existing invitations
          const existingRows = data.invitations.map(inv => ({
            map_id: this.map_id,
            user_id: this.user_id,
            email: inv.email,
            isExisting: true,
            status: inv.status,
            invitation_id: inv.invitation_id
          }));

          // Add one empty row if slots are available
          if (existingRows.length < 5) {
            existingRows.push({
              map_id: this.map_id,
              user_id: this.user_id,
              email: '',
              isExisting: false,
              status: 'ADDED',
              invitation_id: 0
            });
          }

          this.emailRows = existingRows;
        }
        else {
          // Add one empty row if no invitations are found
          this.emailRows = [{
            map_id: this.map_id,
            user_id: this.user_id,
            email: '',
            isExisting: false,
            status: 'ADDED',
            invitation_id: 0
          }];
        }
        this.cdr.detectChanges();
      },
      (error) => {
        this.isFetching = false;
        console.error('Error fetching sent map invitations:', error);
        this.cdr.detectChanges();
      }
    );
  }

  cancelInvitation(invitationId: string | number) {
    this.dataserv.cancelMapInvitation(this.user_id, invitationId).subscribe(
      () => {
        this.toastService.showSuccess('Invitation cancelled.', 'success');
        this.getSentMapInvitations();
        this.cdr.detectChanges();
      },
      (error) => {
        this.toastService.showSuccess(error.error?.message || 'Error canceling invitation', 'danger');
        this.cdr.detectChanges();
      }
    );
  }

  addRow(rowIndex: number) {
    if (this.emailRows.length >= 5) {
      this.toastService.showSuccess('You can invite up to 5 people at a time.', 'danger');
      return;
    }
    this.emailRows.splice(rowIndex, 0, {
      map_id: this.map_id,
      user_id: this.user_id,
      status: 'ADDED',
      email: '',
    });
  }

  deleteRow(index: number) {
    if (this.emailRows.length <= 1 && !this.emailRows[index].isExisting) {
      return;
    }

    const row = this.emailRows[index];
    if (row.isExisting && row.status === 'PENDING') {
      // If it's an existing pending invitation, cancel it first
      this.cancelInvitation(row.invitation_id);
      return; // cancelInvitation will refresh the list on success
    } else if (row.isExisting && row.status === 'ACCEPTED') {
      // If it's an accepted invitation, we don't allow deletion here (handled in UI)
      return;
    }

    if (index >= 0 && index < this.emailRows.length) {
      this.emailRows.splice(index, 1);
    }
  }

  onInputchng(event: any, rowIndex: number): void {
    this.emailinput = event.target.value;

    const value = (this.emailinput || '').trim();
    this.emailalert = '';
    if (!value) return;

    // Say what's wrong instead of silently emptying the box.
    if (!this.isValidEmail(value)) {
      this.emailalert = `"${value}" isn't a valid email address.`;
      return;
    }
    const hasDuplicateEmail = this.emailRows.some(
      (row, index) => index !== rowIndex && (row.email || '').trim().toLowerCase() === value.toLowerCase()
    );
    if (hasDuplicateEmail) {
      this.emailalert = `${value} is already on the list.`;
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  closemodel(isInvited?: boolean) {
    this.activemodel.close(isInvited ? 'invited' : null);
  }

  anyAddedRow(): boolean {
    return (this.emailRows || []).some((row) => row.status === 'ADDED' && (row.email || '').trim());
  }
}
